import { useEffect, useRef, useState } from 'react';
import moment from 'moment';

export const OFF = 'off';
export const UPCOMING = 'upcoming';
export const IN_PROGRESS = 'in_progress';

const PREVIEW_STATES = [UPCOMING, IN_PROGRESS];
const CHECK_INTERVAL_MS = 60 * 1000;

const HAS_UTC_OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/;

const parseTime = (value) =>
  HAS_UTC_OFFSET.test(value)
    ? moment.parseZone(value, moment.ISO_8601, true)
    : moment.invalid();

const maintenanceWindowTimes = ({ start, end }) => {
  if (!start || !end) {
    return null;
  }

  const startTime = parseTime(start);
  const endTime = parseTime(end);

  if (
    !startTime.isValid() ||
    !endTime.isValid() ||
    !endTime.isAfter(startTime)
  ) {
    return null;
  }

  return { startTime, endTime };
};

export const maintenanceState = ({ start, end, now = moment() }) => {
  const times = maintenanceWindowTimes({ start, end });

  if (!times) {
    return OFF;
  }
  if (now.isBefore(times.startTime)) {
    return UPCOMING;
  }
  if (now.isBefore(times.endTime)) {
    return IN_PROGRESS;
  }
  return OFF;
};

export const maintenancePreview = (search) => {
  const requested = new URLSearchParams(search).get('maintenance_preview');
  return PREVIEW_STATES.includes(requested) ? requested : null;
};

export function useMaintenanceState({ start, end, preview, onWindowEnd }) {
  const [state, setState] = useState(() => maintenanceState({ start, end }));
  const previousState = useRef(state);
  const configured = maintenanceWindowTimes({ start, end }) !== null;

  useEffect(() => {
    if ((start || end) && !configured) {
      console.error(
        `Ignoring invalid maintenance window: start=${start} end=${end}`
      );
    }

    const check = () => setState(maintenanceState({ start, end }));
    check();
    const timer = setInterval(check, CHECK_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [start, end, configured]);

  useEffect(() => {
    if (previousState.current === IN_PROGRESS && state === OFF && onWindowEnd) {
      onWindowEnd();
    }
    previousState.current = state;
  }, [state, onWindowEnd]);

  if (state === IN_PROGRESS) {
    return state;
  }
  return configured && preview ? preview : state;
}
